import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hold-to-talk recording for the composer. Starts a MediaRecorder on the
 * microphone, stops on release, and hands back the audio as one Blob for the
 * host to transcribe. Nothing here knows about speech models; the host does.
 *
 * Browsers disagree on containers: Chrome and Firefox record webm/opus,
 * Safari records mp4/aac. Whatever the browser offers is what goes to the
 * host, with its real mime type on the Blob.
 */

export type DictationState = 'idle' | 'starting' | 'recording' | 'transcribing' | 'error'

const CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  return CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t))
}

export function useDictation() {
  const [state, setState] = useState<DictationState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null)

  const supported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined' &&
    !!pickMimeType()

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }

  const start = useCallback(async () => {
    if (!supported || recorderRef.current) return
    setError(null)
    setState('starting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const recorder = new MediaRecorder(stream, { mimeType: pickMimeType() })
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || chunksRef.current[0]?.type || 'audio/webm'
        const blob = chunksRef.current.length ? new Blob(chunksRef.current, { type }) : null
        chunksRef.current = []
        releaseStream()
        recorderRef.current = null
        stopResolveRef.current?.(blob)
        stopResolveRef.current = null
      }
      recorderRef.current = recorder
      recorder.start(250)
      setStartedAt(Date.now())
      setState('recording')
    } catch (err) {
      releaseStream()
      recorderRef.current = null
      setState('error')
      setError(err instanceof Error && err.name === 'NotAllowedError' ? 'Microphone access was refused.' : 'Could not start the microphone.')
    }
  }, [supported])

  /** Stop and return the recording. Resolves null when nothing was captured. */
  const stop = useCallback((): Promise<Blob | null> => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') return Promise.resolve(null)
    return new Promise((resolve) => {
      stopResolveRef.current = resolve
      recorder.stop()
      setStartedAt(null)
    })
  }, [])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      stopResolveRef.current = () => {}
      recorder.stop()
    }
    setStartedAt(null)
    setState('idle')
  }, [])

  useEffect(() => () => releaseStream(), [])

  return { supported, state, setState, error, setError, startedAt, start, stop, cancel }
}
